import { Page } from "../enums/Page";
import { css } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";

const styles: string = css`
    :host {
        display: block;
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 1000;
        background: radial-gradient(circle, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.7));
    }
    .game-instructions {
        position: absolute;
        top: 15%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 2.5rem;
        font-family: "Press Start 2P", cursive;
        color: #f4c842;
        text-align: center;
        text-shadow: 0 0 5px rgba(255, 255, 255, 0.8);
    }
    .game-box {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 350px;
        height: 350px;
        transform: translate(-50%, -50%);
        border: 10px solid #222222;
        border-radius: 50%;
        background: radial-gradient(circle, #444444, #222222);
        display: flex;
        justify-content: center;
        align-items: center;
        box-shadow: 0 0 30px rgba(0, 0, 0, 0.8), inset 0 0 15px rgba(0, 0, 0, 0.6);
    }
    .circle {
        position: absolute;
        width: 70px;
        height: 70px;
        background: radial-gradient(circle, rgba(255, 255, 255, 0.8), rgba(0, 0, 0, 0.2));
        border-radius: 50%;
        border: 3px solid #00FFFF;
        box-shadow: 0 0 15px rgba(255, 255, 255, 0.5);
    }
    .game-over {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 2rem;
        font-weight: bold;
        color: #00ff00;
        display: none;
        text-shadow: 0 0 10px rgba(0, 255, 0, 0.8);
    }
`;

export class BinoMiniGameComponent extends HTMLElement {
    private readonly _gameEventService: GameEventService = new GameEventService();
    private circle: HTMLElement | null = null;
    private animationFrameId: number | null = null;
    private position: number = 0;
    private direction: number = 1;
    private isGameOver: boolean = false;

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        this.render();
        this.startGame();
    }

    private render(): void {
        if (!this.shadowRoot) return;

        this.shadowRoot.innerHTML = `
            <style>${styles}</style>
            <div class="game-instructions">Align the lens in the middle to fix the binoculars</div>
            <div class="game-box">
                <div class="circle"></div>
            </div>
            <div class="game-over">You Win!</div>
        `;
    }

    private startGame(): void {
        this.circle = this.shadowRoot?.querySelector(".circle") ?? null;
        if (!this.circle) return;

        this.position = 0;
        this.direction = 1;
        this.isGameOver = false;

        this.circle.addEventListener("click", () => this.stopGame());
        this.animateCircle();
    }

    private animateCircle(): void {
        if (!this.circle || this.isGameOver) return;

        this.position += this.direction * 2;
        if (this.position >= 250 || this.position <= 0) {
            this.direction *= -1;
        }

        this.circle.style.left = `${this.position}px`;

        this.animationFrameId = requestAnimationFrame(() => this.animateCircle());
    }

    private stopGame(): void {
        if (this.isGameOver || !this.circle) return;

        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }

        const isInMiddle: boolean = this.position >= 120 && this.position <= 180;
        if (isInMiddle) {
            this.showWinMessage();
        }
        else {
            this.restartGame();
        }
    }

    private showWinMessage(): void {
        this.isGameOver = true;
        const gameOverMessage: HTMLElement | null = this.shadowRoot?.querySelector(".game-over") ?? null;
        if (gameOverMessage) {
            gameOverMessage.style.display = "block";
        }

        setTimeout(() => {
            this._gameEventService.switchPage(Page.Canvas);
        }, 1000);
    }

    private restartGame(): void {
        this.startGame();
    }
}
